// The Command Center schedule window: today plus the next six days. Shared by
// the query range (useUpcomingSchedule/useUpcomingBriefs), the day grouping
// (buildUpcomingDays), and the header range label so they stay in lockstep —
// they MUST match, or meetings could be fetched but not shown (or vice versa).
export const UPCOMING_DAYS = 7;

// The Morning Brief window, looking backwards: yesterday and today. Shared by
// the query range (useRecentSchedule/useRecentRecaps) and the day grouping
// (buildRecentDays) for the same reason UPCOMING_DAYS is — a mismatch means
// meetings are fetched but not shown, or shown but never fetched.
export const RECENT_DAYS = 2;

// Outlook Web App compose deep link. Takes `to` and `subject` as query params;
// the body is never passed (see buildOutlookComposeHref).
export const OUTLOOK_COMPOSE_URL =
  "https://outlook.office.com/mail/deeplink/compose";

// Selectable sizes for the "Upcoming briefs" list — how many upcoming meetings
// to show. The backend clamps to this same set, so keep them in sync.
export const UPCOMING_MEETINGS_SIZES = [30, 60, 90] as const;
export const DEFAULT_UPCOMING_MEETINGS_SIZE = 30;

// The sequential subset of Case.Stage__c, in record-type picklist order so it matches
// what advisors see in Salesforce. Salesforce has many more stages than these; any not
// listed renders off-path — no active stepper node, plus a badge carrying the real
// label (see resolveStageProgress).
export const CASE_STAGES = [
  "Not Started",
  "In Process at Our Firm",
  "Request Received",
  "In Progress",
  "Pending Client Input",
  "Waiting on Custodian",
  "Pending Funding",
  "Finalizing Accounts",
  "Completed",
] as const;

// Ordered task stages — a distinct, shorter vocabulary from CASE_STAGES. Same
// exact-match + fallback contract with the backend as above. NOTE: "Deferred"
// and "Canceled" are terminal states, not sequential steps, but the stepper
// renders every stage linearly by design (the mockup shows a Deferred task with
// its earlier stages checked) — so this order is intentional, not a bug.
export const TASK_STAGES = [
  "Not started",
  "In progress",
  "Deferred",
  "Canceled",
  "Completed",
] as const;

// Terminal stages hidden from the "Cases & tasks" card — the card lists OPEN
// work, and terminal items were inflating its counts (EPD-1349). The card only
// ever sees the backend's NORMALIZED `stage` (a CASE_STAGES / TASK_STAGES
// label), never a raw Salesforce status, so this set is in those label terms:
// tasks contribute Completed / Deferred / Canceled; cases contribute Completed.
// These exactly match the backend's SF-verified terminal statuses (Atlas
// search-sf-task). Matched case-insensitively.
//
// Deliberately NOT here: "Closed" is a case *status* the backend filters
// server-side — it never arrives as a `stage`, so the FE can't observe it; and
// British "Cancelled" isn't a real SF picklist value.
export const CLOSED_WORK_ITEM_STAGES: ReadonlySet<string> = new Set([
  "completed",
  "deferred",
  "canceled",
]);
